import * as React from 'react';

import type {User} from 'nutshell-core/users/users-types';

import {SecondarySidebarLoading} from 'shells/navigation/secondary-sidebar/secondary-sidebar-loading';

import {PageTitleBar} from '../../layout';
import {MarketingSidebar as MarketingSidebarNew} from '../../marketing/marketing-sidebar';

const DRIP_SEQUENCE_EDITION_PAGE =
    /email-marketing\/drip-sequences\/[\d]+-emCampaigns\/editions\/[\d]+-emEditions/;
const SMS_DRIP_SEQUENCE_EDITION_PAGE =
    /email-marketing\/sms\/drip-sequences\/[\d]+-smsCampaigns\/editions\/[\d]+-smsEditions/;
const PREPARE_TO_SEND_PAGE =
    /email-marketing\/(broadcasts|newsletters)\/[\d]+-emCampaigns\/editions\/[\d]+-emEditions\/prepare/;
const PREPARE_TO_SEND_PAGE_SMS =
    /email-marketing\/sms\/broadcasts\/[\d]+-smsCampaigns\/editions\/[\d]+-smsEditions\/prepare/;
const FORM_EDITOR_PAGE = /email-marketing\/forms\/[\d]+-wfForms\/editor/;
const FORM_PREVIEW_PAGE = /email-marketing\/forms\/[A-Za-z0-9]{6}\/preview/;
const LANDING_PAGE_EDITOR = /email-marketing\/landing-pages\/[\d]+-lpPages\/editor/;
const SENDER_ADDRESSES_PAGE = /email-marketing\/sender-address/;

const DRIP_SEQUENCE_EDITION_PAGE_2025 =
    /marketing\/email\/drip-sequences\/[\d]+-emCampaigns\/editions\/[\d]+-emEditions/;
const SMS_DRIP_SEQUENCE_EDITION_PAGE_2025 =
    /marketing\/sms\/drip-sequences\/[\d]+-smsCampaigns\/editions\/[\d]+-smsEditions/;
const PREPARE_TO_SEND_PAGE_2025 =
    /marketing\/email\/(broadcasts|newsletters)\/[\d]+-emCampaigns\/editions\/[\d]+-emEditions\/prepare/;
const PREPARE_TO_SEND_PAGE_SMS_2025 =
    /marketing\/sms\/broadcasts\/[\d]+-smsCampaigns\/editions\/[\d]+-smsEditions\/prepare/;
const FORM_EDITOR_PAGE_2025 = /marketing\/forms\/[\d]+-wfForms\/editor/;
const FORM_PREVIEW_PAGE_2025 = /marketing\/forms\/[A-Za-z0-9]{6}\/preview/;
const LANDING_PAGE_EDITOR_2025 = /marketing\/landing-pages\/[\d]+-lpPages\/editor/;
const AI_DESIGN_PAGE =
    /marketing\/email\/(broadcasts|templates|newsletters|drip-sequences)\/[\d]+-emCampaigns\/design\/[\d]+-emEditions/;

/**
 * For some marketing pages, we want to hide the email marketing sidebar. These are usually
 * pages where a full-page editor should take the place of the secondary sidebar
 *
 * @param {*} pathname
 * @returns boolean
 */
export const shouldHideMarketingSidebar = (pathname: string): boolean => {
    return (
        DRIP_SEQUENCE_EDITION_PAGE.test(pathname) ||
        // We hide the NM sidebar when showing the broadcasts or newsletters prepare to send page
        PREPARE_TO_SEND_PAGE.test(pathname) ||
        PREPARE_TO_SEND_PAGE_SMS.test(pathname) ||
        // We hide the NM sidebar when showing the form editor page and the preview page
        FORM_EDITOR_PAGE.test(pathname) ||
        FORM_PREVIEW_PAGE.test(pathname) ||
        LANDING_PAGE_EDITOR.test(pathname) ||
        SMS_DRIP_SEQUENCE_EDITION_PAGE.test(pathname) ||
        DRIP_SEQUENCE_EDITION_PAGE_2025.test(pathname) ||
        SMS_DRIP_SEQUENCE_EDITION_PAGE_2025.test(pathname) ||
        PREPARE_TO_SEND_PAGE_2025.test(pathname) ||
        PREPARE_TO_SEND_PAGE_SMS_2025.test(pathname) ||
        FORM_EDITOR_PAGE_2025.test(pathname) ||
        FORM_PREVIEW_PAGE_2025.test(pathname) ||
        LANDING_PAGE_EDITOR_2025.test(pathname) ||
        AI_DESIGN_PAGE.test(pathname)
    );
};

/**
 * For certain pages, we do not want to show the email marketing sidebar. This function uses the
 * pathname and returns the EmailMarketingSidebar component if it should be shown for that page,
 * or null if it should not.
 *
 * @param pathname
 * @returns {JSX.Element|null}
 */
export const getEmailMarketingSidebar = (
    pathname: string,
    user?: User | null,
    isEmbedded?: boolean
): React.ReactNode | null | undefined => {
    if (!user) {
        return <SecondarySidebarLoading />;
    }

    if (shouldHideMarketingSidebar(pathname)) {
        return null;
    }

    return <MarketingSidebarNew isEmbedded={isEmbedded} user={user} />;
};

/**
 * For certain pages, we may manually set the page title bar. This function uses the pathname
 * and returns the correct PageTitleBar component if this is the case, or null if not.
 *
 * @param pathname
 * @returns {JSX.Element|null}
 */
export const getPageContainerHeader = (pathname: string): React.ReactNode | null | undefined => {
    return SENDER_ADDRESSES_PAGE.test(pathname) ? (
        <PageTitleBar
            title='Sender addresses'
            subheaderPrimaryText='Use these email addresses to send outreach to your contacts'
        />
    ) : null;
};
